import SharedExerciseCard, {formatActivityDate} from './components/ExerciseCard.jsx';
import ThemaNotice from './ThemaNotice.jsx';
import { t, resolveUiLanguage, currentUiLanguage, uiText, withFrench, dateLocale, languageLabel, setUiLanguage } from "../../site/ui-language.js";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  CirclePlay,
  BookOpenText,
  CheckCircle2,
  Clock3,
  Headphones,
  Hash,
  LockKeyhole,
  KeyRound,
  Layers3,
  MessageCircleMore,
  Mic2,
  PenLine,
  Plus,
  Radio,
  Search,
  Users,
  X
} from "lucide-react";
import { versionPresentation } from "./version-labels.js";
import { ExamHeader } from "./components/ExamChrome.jsx";
import CourseInvite from "./CourseInvite.jsx";
import { loadHorenContent } from "./horen-content.js";
import LegacyPage, { preloadLegacyPage } from "./LegacyPage.jsx";
import { getPageConfig } from "./page-config.js";
import { cacheCatalog, getCachedCatalog, getCachedSession, refreshAccount, refreshSession } from "./client-cache.js";

let accountPagesComponent = null;
let accountPagesRequest = null;
let c1Component = null, c1Request = null;
function loadC1() { return c1Component ? Promise.resolve(c1Component) : (c1Request ||= import("./C1App.jsx").then(module => (c1Component = module.default))); }
let courseAppComponent = null;
let courseAppRequest = null;

function loadAccountPages() {
  if (accountPagesComponent) return Promise.resolve(accountPagesComponent);
  if (!accountPagesRequest) {
    accountPagesRequest = import("./AccountPages.jsx").then((module) => {
      accountPagesComponent = module.default;
      return accountPagesComponent;
    });
  }
  return accountPagesRequest;
}

function loadCourseApp() {
  if (courseAppComponent) return Promise.resolve(courseAppComponent);
  if (!courseAppRequest) {
    courseAppRequest = import("./CourseApp.jsx").then((module) => {
      courseAppComponent = module.default;
      return courseAppComponent;
    });
  }
  return courseAppRequest;
}

function DeferredComponent({ load, loaded, componentProps }) {
  const [Component, setComponent] = useState(() => loaded || null);
  useEffect(() => {
    if (Component) return undefined;
    let active = true;
    load().then((nextComponent) => {
      if (active) setComponent(() => nextComponent);
    }).catch(() => {});
    return () => { active = false; };
  }, [Component, load]);
  return Component ? <Component {...componentProps} /> : <ChunkFallback />;
}

function DeferredAccountPages(props) {
  return <DeferredComponent load={loadAccountPages} loaded={accountPagesComponent} componentProps={props} />;
}

function DeferredCourseApp(props) {
  return <DeferredComponent load={loadCourseApp} loaded={courseAppComponent} componentProps={props} />;
}

const ROUTE_MODULES = Object.freeze({
  "/library": "home",
  "/course": "course",
  "/lesen": "lesen",
  "/horen": "horen",
  "/schreiben": "shreiben",
  "/sprechen": "mundlich",
  "/subscription": "subscription",
  "/payment": "payment",
  "/profile": "profile",
  "/dashboard": "dashboard",
  "/dashboard/practice": "dashboard",
  "/dashboard/exams": "dashboard",
  "/dashboard/words": "dashboard",
  "/dashboard/games": "dashboard",
  "/dashboard/texts": "dashboard",
  "/dashboard/progress": "dashboard",
  "/matching-game": "matching_game",
  "/complete-profile": "onboarding",
  "/admin": "admin",
  "/admin/c1": "admin_c1",
  "/c1":"c1_preview",
  "/admin/requests": "admin_requests",
  "/admin/messages": "admin_messages",
  "/correction": "correction"
});

const LEGACY_ROUTES = Object.freeze({
  "/lesen.html": "/lesen",
  "/horen.html": "/horen",
  "/shreiben.html": "/schreiben",
  "/mundlich.html": "/sprechen",
  "/correction.html": "/correction"
});

const ACCOUNT_ROUTES = new Set(["subscription", "payment", "profile", "dashboard", "matching_game", "onboarding", "admin", "admin_requests", "admin_messages", "admin_c1","c1_preview"]);
const modulePreloadPromises = new Map();
const CATALOG_PART_KEYS = Object.freeze(["teil-1", "teil-2", "teil-3"]);
const READING_CATALOG_PART_KEYS = Object.freeze([
  "gesamt",
  "teil-1",
  "teil-2",
  "teil-3",
  "sprachbausteine-1",
  "sprachbausteine-2"
]);
const CATALOG_PART_LABELS = Object.freeze({
  gesamt: "Gesamtprüfung",
  "teil-1": "Teil 1",
  "teil-2": "Teil 2",
  "teil-3": "Teil 3",
  "sprachbausteine-1": "SB 1",
  "sprachbausteine-2": "SB 2"
});
const PREVIEW_COPY = Object.freeze(withFrench({
  ar: {
    direction: "rtl",
    title: "معاينة مجانية",
    description: (limit) => `يمكنك تجربة أول ${limit} تمارين مجاناً. تحتاج الترجمة وملاحظات الذكاء الاصطناعي وبقية التمارين إلى اشتراك فعّال.`,
    login: "تسجيل الدخول",
    register: "أنشئ حساباً مجانياً"
  },
  en: {
    direction: "ltr",
    title: "Free preview",
    description: (limit) => `The first ${limit} exercises are free. Translation, AI feedback, and the remaining exercises require an active subscription.`,
    login: "Log in",
    register: "Create a free account"
  }
}));

const ACCOUNT_REQUIRED_COPY = Object.freeze(withFrench({
  ar: {
    direction: "rtl",
    close: "إغلاق النافذة",
    lockedEyebrow: "تمارين إضافية",
    aiEyebrow: "ميزة بالذكاء الاصطناعي",
    subscriptionTitle: "فعّل اشتراكك للمتابعة",
    accountTitle: "أنشئ حساباً وابدأ مجاناً",
    subscriptionDescription: "هذا التمرين ضمن المكتبة الكاملة. اختر خطة أو فعّل اشتراكك لفتحه.",
    accountDescription: "يمكنك تجربة أول أربعة تمارين من دون تسجيل. تحتاج إلى حساب مجاني لاستخدام الترجمة والشروحات والصوت وملاحظات الذكاء الاصطناعي.",
    plans: "عرض الخطط",
    start: "ابدأ مجاناً",
    login: "لديّ حساب"
  },
  en: {
    direction: "ltr",
    close: "Close dialog",
    lockedEyebrow: "More exercises",
    aiEyebrow: "AI feature",
    subscriptionTitle: "Activate your access",
    accountTitle: "Create an account and start for free",
    subscriptionDescription: "This exercise is part of the full library. Choose a plan or activate your subscription to open it.",
    accountDescription: "You can try the first four exercises without signing in. Create a free account to use translations, explanations, audio, and AI feedback.",
    plans: "View plans",
    start: "Start for free",
    login: "I have an account"
  }
}));

const MODULE_META = Object.freeze({
  lesen: {
    label: "Lesen",
    eyebrow: "Texte verstehen",
    description: "Wählen Sie ein Thema und trainieren Sie alle Teile des Leseverstehens.",
    action: "Thema öffnen",
    route: "/lesen",
    accent: "green",
    icon: BookOpenText
  },
  horen: {
    label: "Hören",
    eyebrow: "Sicher zuhören",
    description: "Trainieren Sie die drei Hörteile mit prüfungsnahen Aussagen und direkter Auswertung.",
    action: "Teil öffnen",
    route: "/horen",
    accent: "blue",
    icon: Headphones
  },
  shreiben: {
    label: "Schreiben",
    eyebrow: "Klar formulieren",
    description: "Wählen Sie eine Schreibaufgabe und erhalten Sie Feedback passend zu Ihrem Niveau.",
    action: "Aufgabe öffnen",
    route: "/schreiben",
    accent: "yellow",
    icon: PenLine
  },
  mundlich: {
    label: "Sprechen",
    eyebrow: "Natürlich sprechen",
    description: "Üben Sie Vorstellen, Diskutieren und gemeinsames Planen für die mündliche Prüfung.",
    action: "Teil öffnen",
    route: "/sprechen",
    accent: "rose",
    icon: MessageCircleMore
  }
});

function currentRoute() {
  const pathname = window.location.pathname.replace(/\/$/, "") || "/library";
  return ROUTE_MODULES[LEGACY_ROUTES[pathname] || pathname] || "home";
}

function currentLevel(moduleKey = currentRoute()) {
  const requested = new URLSearchParams(window.location.search).get("level")?.toLowerCase();
  return requested === "b2" ? "b2" : "b1";
}

function currentListeningType(level = currentLevel()) {
  const requested = new URLSearchParams(window.location.search).get("type")?.toLowerCase();
  if (requested === "codes" || requested === "audio") return requested;
  return level === "b1" ? "audio" : "codes";
}

function currentExercisePage(moduleKey = currentRoute()) {
  if (moduleKey === "correction") return "correction";
  const params = new URLSearchParams(window.location.search);
  const requiredParameter = {
    lesen: "theme",
    horen: "part",
    shreiben: "task",
    mundlich: "part"
  }[moduleKey];
  const fragmentPart = /^#teil-?[123]$/i.test(window.location.hash) ? window.location.hash : "";
  const value = requiredParameter ? params.get(requiredParameter) || (requiredParameter === "part" ? fragmentPart : "") : "";
  if (moduleKey === "mundlich" && value === "rooms") return null;
  return value ? moduleKey : null;
}

function currentCatalogPart(moduleKey = currentRoute()) {
  const params = new URLSearchParams(window.location.search);
  if (moduleKey === "mundlich" && params.get("part") === "rooms") {
    return "rooms";
  }
  const requested = params.get("catalogPart");
  const partKeys = catalogPartKeys(moduleKey);
  return partKeys.includes(requested) ? requested : partKeys[0] || "teil-1";
}

function catalogPartKeys(moduleKey, listeningType = currentListeningType()) {
  if (moduleKey === "lesen" || (moduleKey === "horen" && listeningType === "audio")) return READING_CATALOG_PART_KEYS;
  return moduleKey === "horen" || moduleKey === "mundlich" ? CATALOG_PART_KEYS : [];
}

function normalizeSpaUrl(value) {
  const target = new URL(value, window.location.origin);
  const legacyPath = LEGACY_ROUTES[target.pathname];
  if (legacyPath) target.pathname = legacyPath;
  return target;
}

async function fetchJson(url) {
  const response = await fetch(url, { credentials: "same-origin", cache: "no-store" });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

async function fetchContentJson(url) {
  if (window.ZDeutschDataCache?.fetchJson) {
    return window.ZDeutschDataCache.fetchJson(url);
  }
  return fetchJson(url);
}

function orderedEntries(container, order = []) {
  const entries = container && typeof container === "object" ? container : {};
  const keys = Array.isArray(order) && order.length ? order : Object.keys(entries);
  return keys.filter((key) => entries[key]).map((key) => [key, entries[key]]);
}

function stripMarkdown(value) {
  return String(value || "")
    .replace(/[#*_>`~[\]]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function shorten(value, max = 130) {
  const text = stripMarkdown(value);
  return text.length > max ? `${text.slice(0, max).trim()}…` : text;
}

function readingItems(data, level) {
  const entry = data?.levels?.[level];
  return orderedEntries(entry?.themes, entry?.themeOrder).flatMap(([key, theme], index) => {
    const versions = Array.isArray(theme.versionOrder) && theme.versionOrder.length
      ? theme.versionOrder
      : Object.keys(theme.versions || {});
    const version = theme.defaultVersion || versions[0] || "default";
    const reading = theme.versions?.[version]?.lesen || theme.lesen || {};
    const partKeys = Array.isArray(reading.partOrder) && reading.partOrder.length
      ? reading.partOrder
      : Object.keys(reading.parts || {});
    const parts = partKeys.map((partKey) => [partKey, reading.parts?.[partKey]]);
    const baseHref = `/lesen?level=${encodeURIComponent(level)}&theme=${encodeURIComponent(key)}&version=${encodeURIComponent(version)}`;
    const completeExam = {
      id: `${key}:gesamt`,
      progressKey: key,
      themeKey: key,
      versionKey: version,
      partKey: "gesamt",
      previewIndex: index,
      number: index + 1,
      title: theme.title || key,
      description: `${parts.length || 5} Prüfungsteile${versions.length > 1 ? ` · ${versions.length} Versionen` : ""}`,
      badge: "Gesamt",
      href: baseHref
    };
    const partItems = parts.map(([partKey, part]) => ({
      id: `${key}:${partKey}`,
      progressKey: `${key}:${partKey}`,
      themeKey: key,
      versionKey: version,
      partKey,
      previewIndex: index,
      number: index + 1,
      title: theme.title || key,
      description: shorten(part?.content?.instruction || part?.meta?.title || `Gezieltes Training für ${CATALOG_PART_LABELS[partKey] || partKey}.`),
      badge: CATALOG_PART_LABELS[partKey] || part?.meta?.partLabel || partKey,
      actionLabel: "Teil öffnen",
      href: `${baseHref}&part=${encodeURIComponent(partKey)}&view=part`
    }));
    return [completeExam, ...partItems];
  });
}

function listeningItems(data, level) {
  const levelEntry = data?.levels?.[level];
  let sequence = 0;
  return orderedEntries(levelEntry?.themes, levelEntry?.themeOrder).flatMap(([themeKey, theme]) => {
    const horen = theme?.["hören"];
    return orderedEntries(horen?.parts, horen?.partOrder).flatMap(([partKey, part], partIndex) => {
      const partNumber = partKey.split("-").pop() || String(partIndex + 1);
      const topics = Array.isArray(part?.content?.topics) ? part.content.topics : [];
      if (!topics.length) {
        sequence += 1;
        return [{
          id: `${themeKey}:${partKey}`,
          progressKey: `${themeKey}:${partKey}`,
          partKey,
          number: sequence,
          title: part?.meta?.partLabel || `Teil ${partNumber}`,
          description: shorten(part?.content?.instruction || "Prüfungsnahe Aufgaben zum Hörverstehen."),
          badge: `Teil ${partNumber}`,
          href: `/horen?level=${encodeURIComponent(level)}&type=codes&theme=${encodeURIComponent(themeKey)}&part=${encodeURIComponent(partKey)}`
        }];
      }
      return topics.map((topic, topicIndex) => {
        sequence += 1;
        const topicKey = String(topic?.id || topicIndex + 1);
        return {
          id: `${themeKey}:${partKey}:${topicKey}`,
          progressKey: `${themeKey}:${partKey}:${topicKey}`,
          partKey,
          number: sequence,
          title: topic?.title || topic?.tag || `Thema ${sequence}`,
          description: shorten(topic?.tag || part?.content?.instruction || "Prüfungsnahe Aufgabe zum Hörverstehen."),
          badge: `Teil ${partNumber}`,
          href: `/horen?level=${encodeURIComponent(level)}&type=codes&theme=${encodeURIComponent(themeKey)}&part=${encodeURIComponent(partKey)}&topic=${encodeURIComponent(topicKey)}`
        };
      });
    });
  });
}

function listeningAudioItems(data, level) {
  const levelEntry = data?.levels?.[level];
  return orderedEntries(levelEntry?.themes, levelEntry?.themeOrder).flatMap(([themeKey, theme], index) => {
    const horen = theme?.["hören"];
    const parts = orderedEntries(horen?.parts, horen?.partOrder);
    if (!parts.length) return [];
    const baseHref = `/horen?level=${encodeURIComponent(level)}&type=audio&theme=${encodeURIComponent(themeKey)}`;
    const completeTheme = {
      id: `audio:${themeKey}:gesamt`,
      progressKey: `audio:${themeKey}`,
      themeKey,
      partKey: "gesamt",
      previewIndex: index,
      number: index + 1,
      title: theme.title || themeKey,
      description: `${parts.length} Prüfungsteile · Mit Audio`,
      badge: "Gesamt",
      actionLabel: "Thema öffnen",
      href: `${baseHref}&part=${encodeURIComponent(parts[0][0])}`
    };
    const partItems = parts.map(([partKey, part], partIndex) => {
      const topic = Array.isArray(part?.content?.topics) ? part.content.topics[0] : null;
      const partNumber = partKey.split("-").pop() || String(partIndex + 1);
      return {
        id: `audio:${themeKey}:${partKey}`,
        progressKey: `audio:${themeKey}:${partKey}`,
        themeKey,
        partKey,
        previewIndex: index,
        number: index + 1,
        title: theme.title || themeKey,
        description: shorten(topic?.title || part?.content?.instruction || "Prüfungsnahes Hörverstehen mit Audio."),
        badge: `Teil ${partNumber}`,
        actionLabel: "Teil öffnen",
        href: `${baseHref}&part=${encodeURIComponent(partKey)}&view=part`
      };
    });
    return [completeTheme, ...partItems];
  });
}

function writingItems(data, level) {
  const tasks = data?.levels?.[level]?.tasks || [];
  return tasks.map((task, index) => ({
    id: task.id || `task-${index + 1}`,
    number: index + 1,
    title: task.title || `Aufgabe ${index + 1}`,
    description: shorten(task.istructions || task.content || "Schreibaufgabe", 150),
    badge: level === "b2" ? "Formell" : "E-Mail",
    href: `/schreiben?level=${encodeURIComponent(level)}&task=${encodeURIComponent(task.id || `task-${index + 1}`)}`
  }));
}

function speakingItems(data, levelKey) {
  const level = data?.levels?.[levelKey];
  return orderedEntries(level?.parts, level?.partOrder).flatMap(([partKey, part], partIndex) => {
    const partNumber = partKey.split("-").pop() || String(partIndex + 1);
    const topics = Array.isArray(part?.topics) ? part.topics : [];
    if (!topics.length) {
      return [{
        id: partKey,
        progressKey: partKey,
        partKey,
        number: Number(partNumber) || partIndex + 1,
        title: part.shortTitle || part.title || `Teil ${partNumber}`,
        description: shorten(part.instruction || part.title || "Mündliche Prüfung trainieren.", 150),
        badge: `Teil ${partNumber}`,
        totalCount: (part.prompts || []).length || 1,
        href: `/sprechen?level=${encodeURIComponent(levelKey)}&part=${encodeURIComponent(partKey)}`
      }];
    }
    return topics.map((topic, topicIndex) => ({
      id: `${partKey}:${topic.id || topicIndex + 1}`,
      progressKey: `${partKey}:${topic.id || topicIndex + 1}`,
      partKey,
      number: topicIndex + 1,
      title: topic.title || `Thema ${topicIndex + 1}`,
      description: shorten(topic.prompt || topic.personA?.opinion || part.instruction || "Mündliche Prüfung trainieren.", 150),
      badge: `Teil ${partNumber}`,
      totalCount: 1,
      href: `/sprechen?level=${encodeURIComponent(levelKey)}&part=${encodeURIComponent(partKey)}&topic=${encodeURIComponent(topic.id || topicIndex + 1)}`
    }));
  });
}

async function loadItems(moduleKey, level, listeningType = "codes") {
  if (moduleKey === "lesen") {
    let data;
    try {
      data = await fetchContentJson("/database/lesen-index.json");
    } catch {
      data = await fetchContentJson("/database/lesen.json");
    }
    return readingItems(data, level);
  }
  if (moduleKey === "horen") {
    if (listeningType === "audio") {
      return listeningAudioItems(await fetchContentJson("/database/horen-audio.json"), level);
    }
    return listeningItems(await fetchContentJson("/database/horen-codes.json"), level);
  }
  if (moduleKey === "shreiben") {
    return writingItems(await fetchContentJson("/database/shreiben.json"), level);
  }
  if (moduleKey === "mundlich") {
    return speakingItems(await fetchContentJson("/database/mundlich.json"), level);
  }
  return [];
}

function preloadModule(moduleKey, level, listeningType = "codes") {
  const supportedLevel = level;
  const key = `${moduleKey}:${supportedLevel}:${moduleKey === "horen" ? listeningType : "default"}`;
  if (modulePreloadPromises.has(key)) return modulePreloadPromises.get(key);
  const page = getPageConfig(moduleKey);
  const request = preloadLegacyPage(page.scripts).catch((error) => {
    modulePreloadPromises.delete(key);
    throw error;
  });
  modulePreloadPromises.set(key, request);
  return request;
}

function preloadExercise(item, moduleKey) {
  const target = new URL(item.href, window.location.origin);
  const level = target.searchParams.get("level") || "b1";
  const listeningType = target.searchParams.get("type") === "audio" ? "audio" : "codes";
  const requests = [preloadModule(moduleKey, level, listeningType)];
  if (moduleKey === "horen") {
    requests.push(loadHorenContent({type: listeningType, level, theme: target.searchParams.get("theme") || ""}));
  }
  if (moduleKey === "lesen") {
    const theme = target.searchParams.get("theme");
    if (theme) {
      requests.push(fetchContentJson(`/database/lesen-theme.json?level=${encodeURIComponent(level)}&theme=${encodeURIComponent(theme)}`));
    }
  }
  return Promise.all(requests);
}

function progressModuleKey(moduleKey) {
  return moduleKey === "shreiben" ? "schreiben" : moduleKey;
}

function readLegacyProgress(moduleKey, level, items) {
  try {
    if (moduleKey === "lesen") {
      const store = JSON.parse(window.localStorage.getItem("zdeutsch.lesen.progress.v1") || "{}");
      return items.flatMap((item) => {
        const themeKey = item.themeKey || item.id;
        const versionOptions = Array.isArray(item.versionOptions) && item.versionOptions.length
          ? item.versionOptions
          : [{ versionKey: item.versionKey || "default", progressKey: item.progressKey || item.id }];
        return versionOptions.flatMap((option) => {
          const stored = store[`${level}|${themeKey}|${option.versionKey}`];
          const latest = item.partKey && item.partKey !== "gesamt" ? stored?.partResults?.[item.partKey] : stored;
          if (!latest) return [];
          return [{
            module: "lesen",
            level,
            itemKey: option.progressKey,
            progressPercent: Number(latest.progressPercent ?? 100),
            latestScorePercent: Number(latest.lastPercent || 0),
            latestScoreLabel: `${Number(latest.lastEarnedPoints || 0)} / ${Number(latest.lastMaxPoints || 0)}`,
            attemptCount: Number(latest.attempts || 1),
            lastActivityAt: latest.lastAttemptAt ? new Date(latest.lastAttemptAt).toISOString() : null
          }];
        });
      });
    }
    if (moduleKey === "horen") {
      const store = JSON.parse(window.localStorage.getItem("zdeutsch.horen.progress.v1") || "{}");
      return items.flatMap((item) => {
        const entry = store[`${level}|${item.progressKey || item.id}`];
        if (!entry || typeof entry !== "object") return [];
        return [{
          module: "horen",
          level,
          itemKey: item.progressKey || item.id,
          progressPercent: Number(entry.progressPercent || 0),
          latestScorePercent: Number(entry.latestScorePercent || 0),
          latestScoreLabel: String(entry.latestScoreLabel || ""),
          attemptCount: Number(entry.attemptCount || 1),
          lastActivityAt: entry.lastActivityAt ? new Date(entry.lastActivityAt).toISOString() : null
        }];
      });
    }
    if (moduleKey === "mundlich") {
      const store = JSON.parse(window.localStorage.getItem(`zdeutsch.mundlich.${level}.progress.v1`) || "{}");
      return items.map((item) => {
        const [partKey, topicId] = String(item.progressKey || item.id).split(":");
        const isIntroduction = partKey === "teil-1";
        const completed = isIntroduction
          ? (Array.isArray(store.prepared) ? store.prepared.length : 0)
          : (Array.isArray(store.done?.[partKey]) && store.done[partKey].includes(topicId) ? 1 : 0);
        const total = isIntroduction ? Number(item.totalCount || 0) : 1;
        return {
          module: "mundlich",
          level,
          itemKey: item.progressKey || item.id,
          progressPercent: total ? Math.round((completed / total) * 100) : 0,
          latestScorePercent: null,
          latestScoreLabel: completed ? (isIntroduction ? `${completed} / ${total} vorbereitet` : t("Geübt")) : "",
          attemptCount: 0,
          lastActivityAt: null
        };
      }).filter((entry) => entry.progressPercent > 0);
    }
  } catch {
    return [];
  }
  return [];
}

function mergeProgress(serverProgress, legacyProgress) {
  const merged = new Map();
  (serverProgress || []).forEach((entry) => merged.set(entry.itemKey, entry));
  (legacyProgress || []).forEach((entry) => {
    const current = merged.get(entry.itemKey);
    const currentTime = Date.parse(current?.lastActivityAt || "") || 0;
    const legacyTime = Date.parse(entry.lastActivityAt || "") || 0;
    if (!current || legacyTime > currentTime) {
      merged.set(entry.itemKey, entry);
      fetch("/progress", {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...entry, countsAsAttempt: false })
      }).catch(() => {});
    }
  });
  return Object.fromEntries(merged);
}


function latestExerciseProgress(entries) {
  return (entries || []).filter(Boolean).sort((left, right) => {
    const timeDifference = (Date.parse(right.lastActivityAt || "") || 0) - (Date.parse(left.lastActivityAt || "") || 0);
    if (timeDifference) return timeDifference;
    return Number(right.progressPercent || 0) - Number(left.progressPercent || 0);
  })[0] || null;
}

function progressForItem(item, progressByItem) {
  const versionProgress = (item.versionOptions || []).map((option) => progressByItem[option.progressKey]);
  if (item.legacyProgressKey) versionProgress.push(progressByItem[item.legacyProgressKey]);
  versionProgress.push(progressByItem[item.progressKey || item.id]);
  return latestExerciseProgress(versionProgress);
}

function progressForVersion(item, option, progressByItem) {
  return progressByItem[option.progressKey]
    || (option.versionKey === item.versionKey && item.legacyProgressKey ? progressByItem[item.legacyProgressKey] : null)
    || null;
}

function requestAccountAccess(reason = "ai", authenticated = false) {
  window.dispatchEvent(new CustomEvent("zdeutsch:account-required", {
    detail: { reason, authenticated }
  }));
}

function AccountRequiredDialog({ nativeLanguage = currentUiLanguage() }) {
  const [request, setRequest] = useState(null);
  const language = resolveUiLanguage(nativeLanguage || currentUiLanguage());
  const copy = ACCOUNT_REQUIRED_COPY[language];

  useEffect(() => {
    const handleRequest = (event) => setRequest({
      reason: event.detail?.reason || "ai",
      authenticated: Boolean(event.detail?.authenticated)
    });
    window.addEventListener("zdeutsch:account-required", handleRequest);
    return () => window.removeEventListener("zdeutsch:account-required", handleRequest);
  }, []);

  useEffect(() => {
    if (!request) return undefined;
    const previousOverflow = document.documentElement.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setRequest(null);
    };
    document.documentElement.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.documentElement.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [request]);

  if (!request) return null;
  const subscriptionRequired = request.authenticated && request.reason === "locked";
  const close = () => setRequest(null);
  return (
    <div className="zd-account-required" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
      <section className="zd-account-required__dialog" role="dialog" aria-modal="true" aria-labelledby="zd-account-required-title" dir={copy.direction} lang={language}>
        <button className="zd-account-required__close" type="button" aria-label={copy.close} onClick={close}><X aria-hidden="true" /></button>
        <span className="zd-account-required__icon"><LockKeyhole aria-hidden="true" /></span>
        <p className="zd-account-required__eyebrow">{request.reason === "locked" ? copy.lockedEyebrow : copy.aiEyebrow}</p>
        <h2 id="zd-account-required-title">{subscriptionRequired ? copy.subscriptionTitle : copy.accountTitle}</h2>
        <p>{subscriptionRequired
          ? copy.subscriptionDescription
          : copy.accountDescription}</p>
        <div className="zd-account-required__actions">
          <a className="is-primary" href={subscriptionRequired ? "/subscription" : "/register"}>{subscriptionRequired ? copy.plans : copy.start}</a>
          {!subscriptionRequired ? <a href={`/login?lang=${currentUiLanguage()}`}>{copy.login}</a> : null}
        </div>
      </section>
    </div>
  );
}

function ModuleCard({ moduleKey, level, navigate, language }) {
  const meta = MODULE_META[moduleKey];
  const descriptions = uiText(language, { lesen: "Read texts and answer questions.", horen: "Practise with recordings or statements.", shreiben: "Practise writing and get feedback.", mundlich: "Practise speaking with others." }, { lesen: "اقرأ النصوص وأجب عن الأسئلة.", horen: "استمع إلى التسجيلات أو تدرّب على العبارات.", shreiben: "تدرّب على الكتابة واحصل على تصحيح.", mundlich: "تدرّب على المحادثة مع الآخرين." });
  const Icon = meta.icon;
  const selectedLevel = meta.levels?.includes(level) ? level : meta.levels?.[0] || level;
  const href = `${meta.route}?level=${selectedLevel}`;
  const preload = () => preloadModule(moduleKey, selectedLevel, moduleKey === "horen" ? currentListeningType(selectedLevel) : "codes").catch(() => {});
  return (
    <a
      className={`zd-module-card is-${meta.accent}`}
      href={href}
      onPointerEnter={preload}
      onPointerDown={preload}
      onFocus={preload}
      onClick={(event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigate(href);
      }}
    >
      <div className="zd-module-card__top"><span className="zd-module-card__icon"><Icon aria-hidden="true" /></span><span className="zd-module-card__level">{selectedLevel.toUpperCase()}</span></div>
      <h2>{meta.label}</h2>
      <p>{language ? descriptions[moduleKey] : meta.description}</p>
      <span className="zd-module-card__action">{uiText(language, "Open exercises", "افتح التمارين")} <ArrowRight aria-hidden="true" /></span>
    </a>
  );
}

function ExerciseCard(props) {
  return <SharedExerciseCard {...props} onPreload={props.onPreload || (() => preloadExercise(props.item, props.moduleKey))} onRequestAccess={requestAccountAccess} />;
}

function VersionPickerDialog({ item, progressByItem, navigate, onClose, nativeLanguage }) {
  useEffect(() => {
    const previousOverflow = document.documentElement.style.overflow;
    const closeOnEscape = (event) => { if (event.key === "Escape") onClose(); };
    document.documentElement.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.documentElement.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);

  const language = resolveUiLanguage(nativeLanguage || currentUiLanguage());
  const arabic = language === "ar";
  return (
    <div className="zd-version-picker" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="zd-version-picker__dialog" lang={language} dir={arabic ? "rtl" : "ltr"} role="dialog" aria-modal="true" aria-labelledby="zd-version-picker-title">
        <header>
          <span className="zd-version-picker__icon"><Layers3 aria-hidden="true" /></span>
          <div><p>{uiText(language, "Mehrere Versionen", "اختر النسخة")}</p><h2 id="zd-version-picker-title" dir="auto">{item.title}</h2><span>{uiText(language, "Wählen Sie die Version, die Sie jetzt bearbeiten möchten.", "اختر النسخة التي تريد التدرّب عليها.")}</span></div>
          <button type="button" aria-label={uiText(language, "Dialog schließen", "إغلاق")} onClick={onClose}><X aria-hidden="true" /></button>
        </header>
        <div className="zd-version-picker__list">
          {item.versionOptions.map((option, index) => {
            const presentation = versionPresentation(option, index, nativeLanguage);
            const progress = progressForVersion(item, option, progressByItem);
            const progressPercent = Math.max(0, Math.min(100, Math.round(Number(progress?.progressPercent || 0))));
            const score = progress?.latestScoreLabel || (progress?.latestScorePercent == null ? "" : `${Math.round(progress.latestScorePercent)}%`);
            const activityDate = formatActivityDate(progress?.lastActivityAt);
            return (
              <button key={option.versionKey} type="button" onClick={() => { onClose(); navigate(option.href); }} onPointerEnter={() => preloadExercise(option, "lesen").catch(() => {})}>
                <span className="zd-version-picker__number">{String(index + 1).padStart(2, "0")}</span>
                <span className="zd-version-picker__copy"><strong>{presentation.label}</strong>{option.naming?.exerciseTitle || presentation.title !== item.title ? <small dir="auto">{option.naming?.exerciseTitle || presentation.title}</small> : null}<i><b style={{ width: `${progressPercent}%` }} /></i></span>
                <span className="zd-version-picker__status"><strong>{progressPercent}%</strong><small>{score ? <>{uiText(language, "Letzte Note", "آخر نتيجة")} <bdi dir="ltr">{score}</bdi></> : uiText(language, "Noch nicht begonnen", "لم تبدأ بعد")}{activityDate ? <> · <bdi>{activityDate}</bdi></> : null}</small></span>
                <ArrowRight aria-hidden="true" />
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function roomThemeOptions(items) {
  const options = [{ value: "", label: "Freies Gespräch" }];
  items.forEach((item) => {
    if (!item.partKey) return;
    const topic = new URL(item.href, window.location.origin).searchParams.get("topic")
      || (item.partKey === "teil-1" ? "teil-1" : "");
    if (!topic) return;
    options.push({
      value: `${item.partKey}::${topic}`,
      label: `${item.badge || item.partKey} · ${item.title}`
    });
  });
  return options;
}

function RoomDirectory({ level, items }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [theme, setTheme] = useState("");
  const [roomKey, setRoomKey] = useState("");
  const themes = useMemo(() => roomThemeOptions(items), [items]);

  const loadRooms = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const response = await fetch(`/sprechraeume?level=${encodeURIComponent(level)}`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        cache: "no-store"
      });
      if (response.status === 401 || response.redirected) throw new Error("Bitte melden Sie sich erneut an.");
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Live-Räume konnten nicht geladen werden.");
      setRooms((payload.rooms || []).filter((room) => room.status !== "closed"));
      setError("");
    } catch (loadError) {
      setError(loadError.message || "Live-Räume konnten nicht geladen werden.");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [level]);

  useEffect(() => {
    loadRooms();
    const refreshId = window.setInterval(() => loadRooms({ quiet: true }), 10_000);
    return () => window.clearInterval(refreshId);
  }, [loadRooms]);

  async function createRoom(event) {
    event.preventDefault();
    if (creating) return;
    const [partKey = "", themeId = ""] = theme.split("::");
    setCreating(true);
    setError("");
    try {
      const response = await fetch("/sprechraeume", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ title, level, partKey, themeId, isPublic: true })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) throw new Error(payload.error || "Der Raum konnte nicht erstellt werden.");
      window.location.assign(payload.joinUrl);
    } catch (createError) {
      setError(createError.message || "Der Raum konnte nicht erstellt werden.");
      setCreating(false);
    }
  }

  function openByKey(event) {
    event.preventDefault();
    const normalized = roomKey.replace(/\s+/g, "").toUpperCase();
    if (normalized.length !== 6) {
      setError("Bitte geben Sie einen sechsstelligen Raumschlüssel ein.");
      return;
    }
    window.location.assign(`/sprechraum/key/${encodeURIComponent(normalized)}`);
  }

  return (
    <section className="zd-room-directory" aria-labelledby="zd-room-directory-title">
      <header className="zd-room-directory__head">
        <div>
          <p className="zd-catalog-eyebrow">{t("Live sprechen ·")}{level.toUpperCase()}</p>
          <h2 id="zd-room-directory-title">{t("Sprechräume")}</h2>
          <p>{t("Hören Sie zu, sprechen Sie mit anderen und üben Sie gemeinsam.")}</p>
        </div>
        <form className="zd-room-key-form" onSubmit={openByKey}>
          <KeyRound aria-hidden="true" />
          <input value={roomKey} onChange={(event) => setRoomKey(event.target.value)} maxLength={6} placeholder="Raum-Key" aria-label="Raumschlüssel" />
          <button type="submit">{t("Öffnen")}</button>
        </form>
      </header>

      {createOpen ? (
        <form className="zd-room-create-inline" onSubmit={createRoom}>
          <label>
            <span>{t("Thema")}</span>
            <select value={theme} onChange={(event) => setTheme(event.target.value)}>
              {themes.map((option) => <option key={option.value || "free"} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          <label>
            <span>{t("Raumname")}</span>
            <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={100} placeholder={`${level.toUpperCase()} Sprechtraining`} />
          </label>
          <div className="zd-room-create-inline__actions">
            <button type="button" onClick={() => setCreateOpen(false)}>{t("Abbrechen")}</button>
            <button className="is-primary" type="submit" disabled={creating}>{creating ? "Wird erstellt …" : t("Raum starten")}</button>
          </div>
        </form>
      ) : null}

      {error ? <p className="zd-room-directory__error" role="alert">{error}</p> : null}
      <div className="zd-room-card-grid" aria-live="polite">
        <button type="button" className="zd-room-card is-create" onClick={() => setCreateOpen(true)}>
          <span className="zd-room-card__plus"><Plus aria-hidden="true" /></span>
          <strong>{t("Neuen Raum erstellen")}</strong>
          <small>{t("Ein Thema wählen und loslegen")}</small>
        </button>
        {loading ? Array.from({ length: 2 }, (_, index) => <div key={index} className="zd-room-card is-loading" />) : null}
        {!loading && !rooms.length ? (
          <div className="zd-room-empty-card"><Radio aria-hidden="true" /><strong>{t("Noch keine offenen Räume")}</strong><span>{t("Erstellen Sie den ersten Raum.")}</span></div>
        ) : null}
        {rooms.map((room) => (
          <a className="zd-room-card" key={room.token} href={`/sprechraum/${encodeURIComponent(room.token)}`}>
            <span className="zd-room-card__status"><i />{t("Live")}</span>
            <strong>{room.title}</strong>
            <small>{room.themeTitle || "Freies Gespräch"}</small>
            <span className="zd-room-card__meta">
              <span><Mic2 aria-hidden="true" /> {room.speakerCount || 0}</span>
              <span><Users aria-hidden="true" /> {room.participantCount || 0}</span>
              <span>{t("Beitreten →")}</span>
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

function EmptyState({ isError, onRetry, noSpeakingData = false, level = "" }) {
  return (
    <div className="zd-catalog-empty" role={isError ? "alert" : "status"}>
      <strong>{isError ? "Die Übungen konnten nicht geladen werden." : noSpeakingData ? `Noch keine Sprechthemen für ${level.toUpperCase()}.` : "Keine passenden Übungen gefunden."}</strong>
      <p>{isError ? "Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut." : noSpeakingData ? "Dieses Niveau bleibt ausgewählt. Sobald Inhalte verfügbar sind, erscheinen sie automatisch hier." : "Versuchen Sie einen anderen Suchbegriff."}</p>
      {isError ? <button type="button" onClick={onRetry}>{t("Erneut versuchen")}</button> : null}
    </div>
  );
}

function ChunkFallback() {
  return <div className="zd-chunk-loader" role="status" aria-label="Inhalte werden geöffnet" aria-live="polite"><span /></div>;
}

function SimulationInvite({ level, language = "en" }) {
  const arabic = language === "ar";
  return <section className="zd-simulation-invite" aria-labelledby="zd-simulation-title">
    <span className="zd-simulation-invite__icon" aria-hidden="true"><CirclePlay /></span>
    <div className="zd-simulation-invite__copy">
      <p className="zd-catalog-eyebrow">{uiText(language, "Prüfungssimulation", "محاكاة الامتحان")} · <bdi>{level.toUpperCase()}</bdi></p>
      <h2 id="zd-simulation-title">{uiText(language, "Bereit für eine komplette Prüfung?", "جاهز لامتحان كامل؟")}</h2>
      <p>{uiText(language, "Trainieren Sie mit gemischten Aufgaben, Schreibfeedback und Ihrem ZDeutsch-Übungszertifikat.", "تدرّب على أجزاء متنوعة، واحصل على تقييم الكتابة وشهادة تدريب ZDeutsch.")}</p>
      <div className="zd-simulation-invite__facts"><span>{uiText(language, "9 Teile", "9 أجزاء")}</span><span>{level === "b1" ? "150" : "140"} {uiText(language, "Minuten", "دقيقة")}</span><span dir="ltr">{t("PDF + QR")}</span></div>
    </div>
    <a className="zd-simulation-cta" href={`/simulation?level=${level}`}>{uiText(language, "Simulation starten", "ابدأ المحاكاة")}<ArrowRight aria-hidden="true" /></a>
  </section>;
}

function PracticeLibrary({ level, navigate, language = "en", withSimulation = true }) {
  const arabic = language === "ar";
  return <section id="library" className="zd-dashboard-library" aria-label={uiText(language, "Practice library", "مكتبة التمارين")}>
            <CourseInvite level={level} navigate={navigate} />
            <section className="zd-catalog-hero zd-catalog-hero--home">
              <div>
                <p className="zd-catalog-eyebrow">{t("TELC Deutsch · B1 & B2")}</p>
                <h1>{uiText(language, "Was möchten Sie heute trainieren?", "ماذا تريد أن تتدرّب عليه اليوم؟")}</h1>
                <p>{uiText(language, "Wählen Sie einen Prüfungsbereich und trainieren Sie auf Ihrem Niveau.", "اختر مهارة وابدأ التدريب على مستواك.")}</p>
              </div>
              <div className="zd-catalog-summary" aria-label="Bibliotheksübersicht">
                <strong>4</strong><span>{uiText(language, "Prüfungsbereiche", "مهارات")}</span>
              </div>
            </section>

            <ThemaNotice language={language} level={level} />
            <section className="zd-module-grid" aria-label={t("Prüfungsbereiche")}>
              {Object.keys(MODULE_META).map((moduleKey) => (
                <ModuleCard key={moduleKey} moduleKey={moduleKey} level={level} navigate={navigate} language={language} />
              ))}
            </section>
    {withSimulation ? <SimulationInvite level={level} language={language} /> : null}
  </section>;
}

export default function CatalogApp() {
  const cachedSession = getCachedSession();
  const accountWarmStarted = useRef(false);
  const [route, setRoute] = useState(() => currentRoute());
  const [level, setLevel] = useState(() => currentLevel());
  const [listeningType, setListeningType] = useState(() => currentListeningType());
  const [items, setItems] = useState([]);
  const [prefetchCatalog, setPrefetchCatalog] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [progressByItem, setProgressByItem] = useState({});
  const [query, setQuery] = useState("");
  const [partFilter, setPartFilter] = useState(() => currentCatalogPart());
  const [status, setStatus] = useState("idle");
  const [reloadKey, setReloadKey] = useState(0);
  const [locationRevision, setLocationRevision] = useState(0);
  const [versionPickerItem, setVersionPickerItem] = useState(null);
  const [sessionSnapshot, setSessionSnapshot] = useState(cachedSession);
  const [libraryAccess, setLibraryAccess] = useState(() => ({
    authenticated: Boolean(cachedSession?.authenticated),
    hasFullAccess: cachedSession?.hasFullAccess !== false,
    previewLimit: Number(cachedSession?.previewLimit || 4),
    nativeLanguage: cachedSession?.user?.nativeLanguage || currentUiLanguage()
  }));
  const exercisePage = useMemo(() => currentExercisePage(route), [route, level, locationRevision]);
  const previewCopy = PREVIEW_COPY[resolveUiLanguage(libraryAccess.nativeLanguage || currentUiLanguage())];

  const syncRoute = useCallback(() => {
    const nextRoute = currentRoute();
    const nextLevel = currentLevel(nextRoute);
    setRoute(nextRoute);
    setLevel(nextLevel);
    setListeningType(currentListeningType(nextLevel));
    setQuery("");
    setPartFilter(currentCatalogPart(nextRoute));
    setVersionPickerItem(null);
    setLocationRevision((value) => value + 1);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const navigate = useCallback((href) => {
    if (getCachedSession()?.requiresProfileCompletion && !(getCachedSession()?.canAccessC1&&new URL(href,location.origin).pathname==='/c1')) {
      if (window.location.pathname !== "/complete-profile") window.location.replace("/complete-profile");
      return;
    }
    window.ZDeutschExerciseLocation?.save();
    const target = normalizeSpaUrl(href);
    if (target.pathname === "/dashboard" && target.hash === "#library") { target.pathname = "/dashboard/practice"; target.hash = ""; }
    if (target.pathname === "/library" && getCachedSession()?.authenticated) { target.pathname = "/dashboard/practice"; target.hash = ""; }
    const isCatalogRoute = Boolean(ROUTE_MODULES[target.pathname.replace(/\/$/, "")]);
    if (!isCatalogRoute || target.origin !== window.location.origin) {
      window.location.href = target.href;
      return;
    }
    window.history.pushState({}, "", `${target.pathname}${target.search}${target.hash}`);
    syncRoute();
  }, [syncRoute]);

  useEffect(() => {
    const handlePopState = () => {
      if (getCachedSession()?.requiresProfileCompletion && window.location.pathname !== "/complete-profile" && !(/^\/c1\/?$/.test(window.location.pathname)&&getCachedSession()?.canAccessC1)) {
        window.location.replace("/complete-profile");
        return;
      }
      if (!window.ZDeutschExerciseLocation?.restoreFromHistory()) syncRoute();
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [syncRoute]);

  useEffect(() => {
    const spa = { navigate };
    window.ZDeutschSpa = spa;
    const handleDocumentClick = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest?.("a[href]");
      if (!anchor || anchor.hasAttribute("download") || (anchor.target && anchor.target !== "_self")) return;
      const target = normalizeSpaUrl(anchor.href);
      if (target.origin !== window.location.origin || !ROUTE_MODULES[target.pathname.replace(/\/$/, "")]) return;
      event.preventDefault();
      navigate(target.href);
    };
    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
      if (window.ZDeutschSpa === spa) delete window.ZDeutschSpa;
    };
  }, [navigate]);

  useEffect(() => {
    let active = true;
    refreshSession({ force: true })
      .then((payload) => {
        const user = payload?.user;
        if (!active) return;
        if (payload?.requiresProfileCompletion && window.location.pathname !== "/complete-profile" && !(/^\/c1\/?$/.test(window.location.pathname)&&payload?.canAccessC1)) {
          window.location.replace("/complete-profile");
          return;
        }
        setSessionSnapshot(payload);
        setSessionReady(true);
        setLibraryAccess((current) => ({
          ...current,
          authenticated: Boolean(payload?.authenticated),
          hasFullAccess: payload?.hasFullAccess !== false,
          previewLimit: Number(payload?.previewLimit || current.previewLimit || 4),
          nativeLanguage: user?.nativeLanguage || currentUiLanguage()
        }));
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const user = sessionSnapshot?.user;
    if (!sessionSnapshot?.authenticated || !user?.clarityUserId || typeof window.clarity !== "function") return;
    window.clarity("identify", user.clarityUserId, undefined, window.location.pathname.slice(0, 255));
    if (user.role) window.clarity("set", "zdeutsch_role", user.role);
    if (user.targetLevel) window.clarity("set", "zdeutsch_target_level", user.targetLevel);
  }, [sessionSnapshot, route, level, exercisePage, locationRevision]);

  useEffect(() => {
    if (!sessionSnapshot?.authenticated || !sessionSnapshot?.user || route === "course" || exercisePage || ACCOUNT_ROUTES.has(route) || accountWarmStarted.current) return undefined;
    accountWarmStarted.current = true;
    const warmAccount = () => refreshAccount().catch(() => {});
    const timerId = window.setTimeout(warmAccount, 0);
    return () => window.clearTimeout(timerId);
  }, [sessionSnapshot, route, exercisePage]);

  useEffect(() => {
    if (exercisePage || !MODULE_META[route]) return undefined;
    let idleId = 0;
    let timerId = 0;
    const preload = () => preloadModule(route, level, listeningType).catch(() => {});
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(preload, { timeout: 1800 });
    } else {
      timerId = window.setTimeout(preload, 700);
    }
    return () => {
      if (idleId && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idleId);
      if (timerId) window.clearTimeout(timerId);
    };
  }, [exercisePage, route, level, listeningType]);

  useEffect(() => {
    if (route === "course") {
      document.body.classList.remove("zd-catalog-page");
      setItems([]);
      setStatus("ready");
      return undefined;
    }
    if (exercisePage) {
      document.title = `ZDeutsch | ${MODULE_META[route]?.label || "Korrektur"}`;
      setItems([]);
      setStatus("ready");
      return undefined;
    }
    document.body.classList.add("zd-catalog-page");
    let active = true;
    const accountTitles = {
      subscription: "Mitgliedschaft",
      payment: "Zahlung",
      profile: "Profil",
      dashboard: "Dashboard",
      matching_game: "Matching game",
      admin: "Verwaltung"
    };
    document.title = accountTitles[route]
      ? `ZDeutsch | ${accountTitles[route]}`
      : route === "home"
        ? "ZDeutsch | Prüfungsbibliothek"
        : `ZDeutsch | ${MODULE_META[route]?.label || "Übungen"} ${level.toUpperCase()}`;
    if (ACCOUNT_ROUTES.has(route)) {
      setItems([]);
      setStatus("ready");
      return () => document.body.classList.remove("zd-catalog-page");
    }
    if (route === "home") {
      setItems([]);
      setStatus("ready");
      return () => document.body.classList.remove("zd-catalog-page");
    }
    setPrefetchCatalog(null);
    const catalogModule = progressModuleKey(route);
    const catalogCacheModule = route === "horen" ? `${catalogModule}:${listeningType}` : catalogModule;
    const cachedCatalog = getCachedCatalog(catalogCacheModule, level);
    let hasRenderableItems = Boolean(cachedCatalog?.items?.length);
    let networkApplied = false;
    setStatus(hasRenderableItems ? "ready" : "loading");
    setProgressByItem({});
    const applyCatalog = (payload) => {
      const loadedItems = payload.items || [];
      const legacyProgress = readLegacyProgress(route, level, loadedItems);
      setLibraryAccess({
        authenticated: Boolean(payload.authenticated),
        hasFullAccess: payload.hasFullAccess !== false,
        previewLimit: Number(payload.previewLimit || 4),
        nativeLanguage: getCachedSession()?.user?.nativeLanguage || currentUiLanguage()
      });
      setItems(loadedItems);
      const requestedCatalogPart = currentCatalogPart(route);
      const presentParts = new Set(loadedItems.map((item) => item.partKey));
      setPartFilter(requestedCatalogPart === "rooms" || presentParts.has(requestedCatalogPart)
        ? requestedCatalogPart
        : catalogPartKeys(route, listeningType).find((partKey) => presentParts.has(partKey)) || requestedCatalogPart);
      setProgressByItem(mergeProgress(payload.progress || [], legacyProgress));
      setStatus("ready");
      hasRenderableItems = true;
    };
    if (cachedCatalog) applyCatalog(cachedCatalog);

    if (!cachedCatalog) {
      loadItems(route, level, listeningType).then((localItems) => {
        if (!active || networkApplied || !localItems?.length) return;
        const previewLimit = Number(libraryAccess.previewLimit || 4);
        const localPayload = {
          authenticated: libraryAccess.authenticated,
          hasFullAccess: libraryAccess.hasFullAccess,
          previewLimit,
          nativeLanguage: libraryAccess.nativeLanguage,
          items: localItems.map((item, index) => ({
            ...item,
            locked: libraryAccess.hasFullAccess === false && Number(item.previewIndex ?? index) >= previewLimit
          })),
          progress: []
        };
        applyCatalog(localPayload);
      }).catch(() => {});
    }

    const itemsRequest = fetchJson(`/catalog-data?module=${encodeURIComponent(catalogModule)}&level=${encodeURIComponent(level)}${route === "horen" ? `&type=${encodeURIComponent(listeningType)}` : ""}`);
    itemsRequest
      .then((payload) => {
        if (!active) return;
        networkApplied = true;
        cacheCatalog(catalogCacheModule, level, payload);
        setPrefetchCatalog({route, level, listeningType, items:payload.items || []});
        applyCatalog(payload);
      })
      .catch((error) => {
        console.warn("Catalog background refresh failed.", error);
        if (active && !hasRenderableItems) setStatus("error");
      });
    return () => {
      active = false;
      document.body.classList.remove("zd-catalog-page");
    };
  }, [exercisePage, route, level, listeningType, reloadKey]);

  useEffect(() => {
    // Only a fresh server catalog can schedule downloads. Local labels may be stale.
    if (!sessionReady || exercisePage || !prefetchCatalog || prefetchCatalog.route !== route || prefetchCatalog.level !== level || prefetchCatalog.listeningType !== listeningType) return;
    const cache = window.ZDeutschDataCache;
    if (!cache?.startContentPrefetch) return;
    const eligible = prefetchCatalog.items.filter(item => item.locked === false);
    if (!eligible.length) return;
    const ordered = [...eligible.filter(item => item.partKey === partFilter), ...eligible.filter(item => item.partKey !== partFilter)];
    const urls = ['/database/config.json'];
    if (route === 'lesen') urls.push('/database/lesen-index.json');
    for (const item of ordered) {
      const target = new URL(item.href, location.origin), params = target.searchParams;
      if (route === 'lesen' && params.get('theme')) urls.push(`/database/lesen-theme.json?${new URLSearchParams({level,theme:params.get('theme')})}`);
      if (route === 'horen' && params.get('theme')) urls.push(`/database/horen-theme.json?${new URLSearchParams({type:listeningType,level,theme:params.get('theme')})}`);
    }
    if (route === 'shreiben') urls.push('/database/shreiben.json');
    if (route === 'mundlich') urls.push('/database/mundlich.json');
    const job = cache.startContentPrefetch(urls);
    return () => job.cancel();
  }, [sessionReady, exercisePage, prefetchCatalog, route, level, listeningType, partFilter]);

  const availablePartKeys = useMemo(() => {
    if (!['lesen', 'horen', 'mundlich'].includes(route)) return [];
    const present = new Set(items.map((item) => item.partKey).filter(Boolean));
    const order = catalogPartKeys(route, listeningType);
    return order.filter((partKey) => present.has(partKey));
  }, [items, route, listeningType]);

  useEffect(() => {
    if (partFilter !== "rooms" && availablePartKeys.length && !availablePartKeys.includes(partFilter)) {
      setPartFilter(availablePartKeys[0]);
    }
  }, [availablePartKeys, partFilter]);

  const selectPartFilter = useCallback((nextPart) => {
    setPartFilter(nextPart);
    const target = new URL(window.location.href);
    // Keep catalog filters separate from the "part" parameter that opens an exercise.
    if (route === "mundlich" && nextPart === "rooms") {
      target.searchParams.set("part", "rooms");
      target.searchParams.delete("catalogPart");
    } else {
      target.searchParams.set("catalogPart", nextPart);
      if (route === "mundlich") target.searchParams.delete("part");
    }
    if (target.href !== window.location.href) {
      window.history.pushState({}, "", `${target.pathname}${target.search}${target.hash}`);
    }
  }, [route]);

  const selectListeningType = useCallback((nextType) => {
    if (nextType === listeningType) return;
    const target = new URL(window.location.href);
    target.pathname = "/horen";
    target.searchParams.set("level", level);
    target.searchParams.set("type", nextType);
    ["theme", "part", "topic", "view", "catalogPart"].forEach((key) => target.searchParams.delete(key));
    navigate(`${target.pathname}${target.search}`);
  }, [level, listeningType, navigate]);

  const filteredItems = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("de");
    return items.filter((item) => {
      if (availablePartKeys.length && item.partKey !== partFilter) return false;
      if (!needle) return true;
      return `${item.title} ${uiText(currentUiLanguage(), item.description)} ${item.badge} ${item.collectionTitle || ""} ${(item.aliases || []).join(" ")} ${(item.searchNames || []).join(" ")}`.toLocaleLowerCase("de").includes(needle);
    });
  }, [availablePartKeys, items, partFilter, query]);
  const activePartItemCount = useMemo(() => (
    availablePartKeys.length ? items.filter((item) => item.partKey === partFilter).length : items.length
  ), [availablePartKeys, items, partFilter]);


  useEffect(() => {
    if (route !== "home" || !sessionSnapshot?.authenticated) return;
    const target = new URL(window.location.href);
    target.pathname = "/dashboard/practice"; target.hash = "";
    window.history.replaceState({}, "", `${target.pathname}${target.search}${target.hash}`);
    syncRoute();
  }, [route, sessionSnapshot, syncRoute]);

  useEffect(() => {
    if (route !== "dashboard" || window.location.hash !== "#library") return;
    const target = new URL(window.location.href); target.pathname = "/dashboard/practice"; target.hash = "";
    window.history.replaceState({}, "", `${target.pathname}${target.search}`); syncRoute();
  }, [route, locationRevision, syncRoute]);

  const meta = MODULE_META[route];

  if (["admin_c1","c1_preview"].includes(route)) return <DeferredComponent load={loadC1} loaded={c1Component} componentProps={{session:sessionSnapshot,sessionReady,navigate,locationRevision}} />;

  if (route === "course") {
    return <DeferredCourseApp key={locationRevision} onExit={navigate} session={sessionSnapshot} />;
  }

  if (exercisePage) {
    const page = getPageConfig(exercisePage);
    return (
      <>
        <LegacyPage
          key={`${exercisePage}:${level}:${locationRevision}`}
          pageName={exercisePage}
          markup={page.markup}
          scripts={page.scripts}
          bodyClassName={page.bodyClassName}
          onNavigate={navigate}
        />
        <AccountRequiredDialog nativeLanguage={libraryAccess.nativeLanguage} />
      </>
    );
  }

  if (ACCOUNT_ROUTES.has(route)) {
    return <><DeferredAccountPages key={route} route={route} dashboardPage={route === "dashboard" ? window.location.pathname.split("/")[2] || "overview" : route === "matching_game" ? "games" : "account"} onNavigate={navigate} sessionUser={sessionSnapshot?.user} renderLibrary={(targetLevel, language) => <PracticeLibrary level={targetLevel} navigate={navigate} language={language} withSimulation={false} />} renderSimulationInvite={(targetLevel, language) => <SimulationInvite level={targetLevel} language={language} />} /><AccountRequiredDialog nativeLanguage={libraryAccess.nativeLanguage} /></>;
  }

  return (
    <div className="zd-catalog-app">
      <ExamHeader pageName={route} catalog onNavigate={navigate} />
      <main className={`zd-catalog-main${route !== "home" ? " is-exercise-catalog" : ""}`} id="catalog-content">
        {route === "home" ? (
          <>
            <PracticeLibrary level={level} navigate={navigate} language={resolveUiLanguage(libraryAccess.nativeLanguage)} />
          </>
        ) : (
          <>
            <h1 className="sr-only">{meta.label}{t("-Übungen ·")}{level.toUpperCase()}</h1>
            <ThemaNotice language={resolveUiLanguage(currentUiLanguage())} module={route} level={level} />

            {route === "horen" ? (
              <nav className="zd-listening-source-tabs" aria-label="Hören-Übungstyp" role="tablist">
                <button type="button" role="tab" aria-selected={listeningType === "audio"} className={listeningType === "audio" ? "is-active" : ""} onClick={() => selectListeningType("audio")}>
                  <CirclePlay aria-hidden="true" />
                  <span><strong>{t("Mit Audio")}</strong><small>{t("Komplette Themen")}</small></span>
                </button>
                <button type="button" role="tab" aria-selected={listeningType === "codes"} className={listeningType === "codes" ? "is-active" : ""} onClick={() => selectListeningType("codes")}>
                  <Hash aria-hidden="true" />
                  <span><strong>{t("Codes")}</strong><small>{t("Richtig oder falsch")}</small></span>
                </button>
              </nav>
            ) : null}

            {partFilter !== "rooms" ? <section className="zd-catalog-toolbar" aria-label={t("Übungen filtern")}>
              <label className="zd-catalog-search">
                <Search aria-hidden="true" />
                <span className="sr-only">{t("Übungen durchsuchen")}</span>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={uiText(currentUiLanguage(), `Search ${meta.label} exercises`)}
                />
              </label>
              <span className="zd-catalog-result-count">
                {status === "ready" ? uiText(currentUiLanguage(), `${filteredItems.length} of ${activePartItemCount}`) : t("Loading…")}
              </span>
            </section> : null}

            {!libraryAccess.hasFullAccess ? (
              <aside className="zd-preview-notice" dir={previewCopy.direction} lang={resolveUiLanguage(libraryAccess.nativeLanguage)}>
                <div>
                  <strong>{previewCopy.title}</strong>
                  <span>{previewCopy.description(libraryAccess.previewLimit)}</span>
                </div>
                <span className="zd-preview-notice__actions">
                  <a href={`/login?lang=${currentUiLanguage()}`}>{previewCopy.login}</a>
                  <a className="is-primary" href={`/register?lang=${currentUiLanguage()}`}>{previewCopy.register}</a>
                </span>
              </aside>
            ) : null}

            {availablePartKeys.length ? (
              <nav className={`zd-catalog-part-tabs${route === "mundlich" ? " has-live-room" : ""}${route === "lesen" || (route === "horen" && listeningType === "audio") ? " has-reading-parts" : ""}`} aria-label={`${meta.label} Prüfungsteile`} role="tablist">
                {availablePartKeys.map((partKey) => {
                  const count = items.filter((item) => item.partKey === partKey).length;
                  const selected = partFilter === partKey;
                  return (
                    <button
                      key={partKey}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      className={selected ? "is-active" : ""}
                      onClick={() => selectPartFilter(partKey)}
                    >
                      <span>{CATALOG_PART_LABELS[partKey] || `Teil ${partKey.slice(-1)}`}</span>
                      <strong>{count}</strong>
                    </button>
                  );
                })}
                {route === "mundlich" ? (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={partFilter === "rooms"}
                    className={`is-live-room${partFilter === "rooms" ? " is-active" : ""}`}
                    onClick={() => {
                      if (!libraryAccess.hasFullAccess) {
                        navigate(libraryAccess.authenticated ? "/subscription" : "/login");
                        return;
                      }
                      selectPartFilter("rooms");
                    }}
                  >
                    <span><Radio aria-hidden="true" />{t("Live-Räume")}</span>
                    <strong>{libraryAccess.hasFullAccess ? "+" : <LockKeyhole aria-hidden="true" />}</strong>
                  </button>
                ) : null}
              </nav>
            ) : null}

            {partFilter === "rooms" && libraryAccess.hasFullAccess ? (
              <RoomDirectory level={level} items={items} />
            ) : status === "loading" ? (
              <div className="zd-catalog-grid" aria-label="Übungen werden geladen">
                {Array.from({ length: 6 }, (_, index) => <div key={index} className="zd-exercise-skeleton" />)}
              </div>
            ) : status === "error" ? (
              <EmptyState isError onRetry={() => setReloadKey((value) => value + 1)} />
            ) : filteredItems.length ? (
              <section className="zd-catalog-grid" aria-label={`${meta.label}-Übungen`}>
                {filteredItems.map((item) => <ExerciseCard key={item.id} item={item} meta={meta} moduleKey={route} navigate={navigate} progress={progressForItem(item, progressByItem)} authenticated={libraryAccess.authenticated} onOpenVersions={setVersionPickerItem} />)}
              </section>
            ) : (
              <EmptyState noSpeakingData={route === "mundlich" && items.length === 0} level={level} />
            )}
          </>
        )}
      </main>
      <footer className="zd-catalog-footer">
        <img src="logo.svg?v=20260312-1" alt="ZDeutsch" />
        <span>{t("TELC Deutschvorbereitung · B1 & B2")}</span>
        <a href="/library" onClick={(event) => { event.preventDefault(); navigate(`/library?level=${level}`); }}>{t("Bibliothek")}</a>
      </footer>
      {versionPickerItem ? <VersionPickerDialog nativeLanguage={libraryAccess.nativeLanguage} item={versionPickerItem} progressByItem={progressByItem} navigate={navigate} onClose={() => setVersionPickerItem(null)} /> : null}
      <AccountRequiredDialog nativeLanguage={libraryAccess.nativeLanguage} />
    </div>
  );
}
